package ai.jessie.web.work

import ai.jessie.shared.work.WorkResponse
import ai.jessie.shared.work.WorkSelectionsResponse
import ai.jessie.web.members.deniesAccess
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

data class WorkUiState(
    val entries: List<WorkResponse>? = null,
    val current: WorkResponse? = null,
    val selections: WorkSelectionsResponse? = null,
    val draft: WorkDraft? = null,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
    val problems: Map<String, String> = emptyMap(),
    val notice: String? = null,
)

data class WorkActions(
    val refresh: () -> Unit,
    val create: () -> Unit,
    val edit: () -> Unit,
    val change: ((WorkDraft) -> WorkDraft) -> Unit,
    val submit: () -> Unit,
    val cancel: () -> Unit,
    val close: () -> Unit,
)

/**
 * UC-008 management. Without [workId] the state lists work entries and creates one; with it the
 * state shows and edits that entry. Single-flight; a disposed page scope cannot publish late
 * success, error or finalisation, so a change of organisation never shows the previous tenant's
 * answer. Nothing is sent before [submit].
 */
class WorkState(
    private val port: WorkPort,
    private val scope: CoroutineScope,
    private val organisationId: String,
    private val workId: String? = null,
    private val preset: WorkPreset? = null,
) {
    var state by mutableStateOf(WorkUiState())
        private set

    val actions =
        WorkActions(
            refresh = ::refresh,
            create = { if (workId == null) open { it.newDraft(preset) } },
            edit = {
                open { selections -> state.current?.takeIf { workId != null }?.draft(selections) }
            },
            change = { update ->
                val draft = state.draft
                if (!state.busy && draft != null) state = state.copy(draft = update(draft))
            },
            submit = ::submit,
            // A4: cancelling sends nothing.
            cancel = {
                if (!state.busy)
                    state =
                        state.copy(
                            draft = null,
                            error = null,
                            problems = emptyMap(),
                            notice = "Abgebrochen. Es wurde nichts gespeichert.",
                        )
            },
            close = {
                if (!state.busy) {
                    state = WorkUiState()
                    refresh()
                }
            },
        )

    // Step 2: entries together with the permitted choices that name their references.
    fun refresh() {
        if (state.draft != null) return
        request {
            val selections = port.selections(organisationId)
            if (workId == null) {
                val entries = port.list(organisationId)
                currentCoroutineContext().ensureActive()
                state = state.copy(entries = entries, selections = selections)
            } else {
                val current = port.read(organisationId, workId)
                currentCoroutineContext().ensureActive()
                state = state.copy(current = current, selections = selections)
            }
        }
    }

    // Steps 4 and 9: the form opens only with the choices the server offers right now.
    private fun open(draft: (WorkSelectionsResponse) -> WorkDraft?) {
        if (state.busy || state.draft != null) return
        request {
            val selections = port.selections(organisationId)
            currentCoroutineContext().ensureActive()
            val opened = draft(selections) ?: return@request
            state = state.copy(selections = selections, draft = opened)
        }
    }

    private fun submit() {
        val draft = state.draft
        if (state.busy || draft == null) return
        val unsendable = draft.unsendableFields().mapValues { workCorrection(it.key, it.value) }
        val proposal = draft.request(state.current?.takeIf { workId != null }?.revision)
        if (unsendable.isNotEmpty() || proposal == null) {
            state = state.copy(problems = unsendable, error = workValidationProblem(unsendable))
            return
        }
        request(saving = true) {
            val saved = port.save(organisationId, workId, proposal)
            currentCoroutineContext().ensureActive()
            state =
                WorkUiState(
                    current = saved,
                    selections = state.selections,
                    busy = true,
                    notice = "Arbeitseintrag gespeichert.",
                )
        }
    }

    private fun request(saving: Boolean = false, block: suspend () -> Unit) {
        if (state.busy) return
        state = state.copy(busy = true, error = null, problems = emptyMap(), notice = null)
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: WorkRequestFailure) {
                currentCoroutineContext().ensureActive()
                rejected(failure, saving)
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                // Only an unanswered write leaves the stored state unknown; reads changed nothing.
                state =
                    state.copy(error = if (saving) uncertainWorkProblem() else unavailableProblem())
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    private suspend fun rejected(failure: WorkRequestFailure, saving: Boolean) {
        val detail = failure.detail
        val error = failure.workProblem(saving)
        val stale = detail.current
        val selections = state.selections
        when {
            // A2: the entered values stay while the permitted choices are loaded again.
            saving && workId == null && failure.status == HTTP_NOT_FOUND -> {
                state = state.copy(error = error)
                reloadSelections()
            }
            // A5, A10 and tenant denial: nothing previously loaded stays on the page.
            OnboardingRequestFailure(failure.status, detail.code).deniesAccess() ->
                state = WorkUiState(busy = true, error = error)
            // A8: the current entry replaces the displayed one and is offered for correction.
            detail.code == "STALE_WORK" ->
                state =
                    if (stale != null && selections != null)
                        state.copy(current = stale, draft = stale.draft(selections), error = error)
                    else WorkUiState(busy = true, error = error)
            // A1, A7: the entered values stay with every correction marked.
            detail.code == "INVALID_WORK" -> {
                val problems =
                    detail.problems.associate { it.field to workCorrection(it.field, it.reason) }
                state = state.copy(problems = problems, error = workValidationProblem(problems))
            }
            // A3, A9: the entered values stay for another attempt.
            else -> state = state.copy(error = error)
        }
    }

    private suspend fun reloadSelections() {
        try {
            val selections = port.selections(organisationId)
            currentCoroutineContext().ensureActive()
            state = state.copy(selections = selections, draft = state.draft?.permitted(selections))
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: WorkRequestFailure) {
            currentCoroutineContext().ensureActive()
            state = WorkUiState(busy = true, error = failure.workProblem(saving = false))
        } catch (_: Exception) {
            currentCoroutineContext().ensureActive()
            state = WorkUiState(busy = true, error = unavailableProblem())
        }
    }
}

private const val HTTP_NOT_FOUND = 404
